// =============================================================================
// Admin Header — Top bar with page title, admin badge, and user menu
// =============================================================================

import { useLocation, useNavigate } from 'react-router';
import { hasAnyPermission } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import {
  Badge,
  Avatar,
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
} from '@tkpod/ui';

interface AdminHeaderProps {
  onMenuClick: () => void;
}

/**
 * Page title resolution for the header.
 * Entries are checked top-to-bottom; first match wins.
 * Exact matches are checked first, then prefix matches.
 */
const exactTitles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/tenants': 'Tenants',
  '/orders': 'Orders',
  '/catalog': 'Product Catalog',
  '/catalog/new': 'Create Template',
  '/jobs': 'Job Queue',
  '/admin-users': 'Admin Users',
  '/roles': 'Roles',
  '/roles/new': 'Create Role',
  '/platform-users': 'Platform Users',
  '/variant-options': 'Variant Options',
  '/colors': 'Color Library',
  '/cost-manager': 'Cost Manager',
  '/providers': 'Providers',
  '/plans': 'Plans',
  '/wallets': 'Wallets',
  '/billing/tax-exemptions': 'Tax exemptions',
  '/subscriptions': 'Subscriptions',
  '/reconciliation': 'Reconciliation',
  '/portal-roles': 'Portal Roles',
  '/settings': 'Settings',
  '/profile': 'Profile',
};

/**
 * Prefix-based titles for dynamic routes (e.g. /roles/:id/edit).
 * Ordered from most specific to least specific prefix.
 */
const prefixTitles: [prefix: string, title: string][] = [
  ['/roles/', 'Role Details'],
  ['/admin-users/', 'Admin User Details'],
  ['/platform-users/', 'User Details'],
  ['/tenants/', 'Tenant Details'],
  ['/catalog/', 'Template Details'],
  ['/variant-options/', 'Option Type Details'],
  ['/providers/', 'Provider Details'],
  ['/orders/', 'Order Details'],
];

function resolvePageTitle(pathname: string): string {
  // 1. Exact match
  const exact = exactTitles[pathname];
  if (exact) return exact;

  // 2. Special suffix patterns (e.g. /roles/:id/edit)
  if (pathname.endsWith('/edit')) {
    if (pathname.startsWith('/roles/')) return 'Edit Role';
  }

  // 3. Prefix match (most specific first)
  for (const [prefix, title] of prefixTitles) {
    if (pathname.startsWith(prefix)) return title;
  }

  return 'Admin';
}

export function AdminHeader({ onMenuClick }: AdminHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const title = resolvePageTitle(location.pathname);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Admin';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <header className="flex h-16 items-center justify-between border-b border-border bg-surface px-6">
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <button
          type="button"
          onClick={onMenuClick}
          className="rounded-md p-2 text-text-secondary hover:bg-surface-tertiary lg:hidden"
          aria-label="Open sidebar"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>

        {/* Page title + Admin badge */}
        <h1 className="text-lg font-semibold text-text-primary">{title}</h1>
        <Badge variant="danger" size="sm">Admin</Badge>
      </div>

      {/* Right side — User menu */}
      <div className="flex items-center gap-3">
        <DropdownMenu align="right">
          <DropdownTrigger className="rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2">
            <Avatar
              src={user?.avatar}
              name={displayName}
              size="sm"
            />
            <svg className="ml-1 h-4 w-4 text-text-tertiary" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
            </svg>
          </DropdownTrigger>

          <DropdownContent>
            {/* User info header */}
            <div className="px-3 py-2.5 border-b border-border">
              <p className="text-sm font-medium text-text-primary truncate">{displayName}</p>
              <p className="text-xs text-text-tertiary truncate">{user?.email}</p>
              {user?.currentOrganization && (
                <p className="mt-0.5 text-xs text-text-tertiary truncate">
                  {user.currentOrganization.name}
                </p>
              )}
            </div>

            <DropdownItem
              icon={<UserIcon />}
              onClick={() => navigate('/profile')}
            >
              Profile
            </DropdownItem>

            {hasAnyPermission(user?.permissions ?? user?.currentOrganization?.permissions ?? [], ['settings:read', 'settings:manage']) && (
              <DropdownItem
                icon={<SettingsIcon />}
                onClick={() => navigate('/settings')}
              >
                Settings
              </DropdownItem>
            )}

            <DropdownSeparator />

            <DropdownItem
              icon={<LogoutIcon />}
              danger
              onClick={handleLogout}
            >
              Sign Out
            </DropdownItem>
          </DropdownContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function UserIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
    </svg>
  );
}
